import { debounce } from 'lodash';
import * as ProspectingManual from './ProspectingManual';
import * as ProspectingExisting from './ProspectingExisting';
import RequestBuilder, { comparisonTypes } from 'Resources/RequestBuilder';
import Prospecting from 'App/babel/resources/Prospecting';
import logError from 'App/babel/helpers/logError';
import NotificationService from 'App/babel/NotificationService';
import T from 'Components/Helpers/translate';
import { NumberFormat } from 'App/babel/utils/numberFormat';

export const RESET = '[ProspectingShared] Reset';
export const RESET_SEARCH = '[ProspectingShared] Soft reset';
export const SET_QUICK_SERCH_VALUE = '[ProspectingShared] Set quickSearch value';
export const SET_ACTIVE_TAB = '[ProspectingShared] Set active tab';
export const SET_SELECT_HEADER_TAB = '[ProspectingShared] Set select header tab';
export const UPDATE_VIEW = '[ProspectingShared] Update view';
export const SET_SELECTION = '[ProspectingShared] Set selection';
export const SET_COUNTRIES_IN_SELECTION = '[ProspectingShared] Set countries in selection';
export const SET_SELECTED_COUNTRY_IN_RESULT = '[ProspectingShared] Set selected country in result';
export const SET_ALL_RESULTS = '[ProspectingShared] Set all results';

export const LIMIT = 100;

/** Mirrors MAX_SEARCH_ADD in base prospecting/helpers.ts. */
export const MAX_SEARCH_ADD = 50;

/** Mirrors MAX_BULK_IDS in base prospecting/helpers.ts. */
export const MAX_SELECTED = 2000;

/** Only prospecting ids reach the prospecting service; the Existing drawer's client ids do not. */
const selectionLimit = idField => (idField === 'prospectingId' ? MAX_SELECTED : Infinity);

export const warnSelectionLimit = () =>
	NotificationService.add({
		style: NotificationService.style.WARN,
		title: 'default.warning',
		body: T('companyMap.select.limit', { count: new NumberFormat().default(MAX_SELECTED) })
	});

export const Tabs = {
	Search: 'Search',
	Selected: 'Selected'
};

export const Steps = {
	Search: 'Search',
	Manage: 'Manage'
};

export const initialState = {
	[Tabs.Search]: {
		data: [],
		total: 0,
		offset: 0,
		loading: false,
		sorting: 'name',
		asc: true
	},
	[Tabs.Selected]: {
		data: [],
		total: 0,
		offset: 0,
		loading: false,
		sorting: 'name',
		asc: true
	},
	activeTab: Tabs.Search,
	selectedHeaderTab: Steps.Search,
	quickSearchValue: '',
	countriesInSelection: {},
	selectedCountryInResult: null,
	selection: {},
	/** Set while step 2 adds every company the search matches: the search itself, not an id list. */
	allResults: null
};

const tabMap = {
	Existing: ProspectingExisting,
	Manually: ProspectingManual
};

const forwardCall =
	fn =>
	(...args) =>
	(dispatch, getState) => {
		const { selectedDrawer } = getState().Prospecting;
		dispatch(tabMap[selectedDrawer][fn](...args));
	};
const getIdField = () => (dispatch, getState) => {
	const { selectedDrawer } = getState().Prospecting;
	switch (selectedDrawer) {
		case 'Existing':
			return 'id';
		default:
			return 'prospectingId';
	}
};
const _search = forwardCall('search');
const search = () => debounce(dispatch => dispatch(_search()), 250);
const _searchSelected = forwardCall('searchSelected');
const searchSelected = () => debounce(dispatch => dispatch(_searchSelected()), 250);

export const onHeaderTabChanged = selectedHeaderTab => ({ type: SET_SELECT_HEADER_TAB, selectedHeaderTab });

export const setSelectedResultCountryTab = selectedCountryInResult => dispatch => {
	dispatch({ type: SET_SELECTED_COUNTRY_IN_RESULT, selectedCountryInResult });
	dispatch({ type: UPDATE_VIEW, view: Tabs.Selected, offset: 0 });
	dispatch(searchSelected());
};

function existing(idField, item) {
	if (idField === 'id') {
		return true;
	}
	return !!item.matchInUpsales;
}

export const handleCountriesInSelection = (nrOfAffectedCompanies, selectedCountry) => (dispatch, getState) => {
	const { countriesInSelection, selectedCountryInResult } = getState().ProspectingShared;

	if (selectedCountry) {
		if (!selectedCountryInResult) {
			dispatch({ type: SET_SELECTED_COUNTRY_IN_RESULT, selectedCountryInResult: selectedCountry });
		}
		const nrOfSelectedCompanies = (countriesInSelection[selectedCountry] || 0) + nrOfAffectedCompanies;
		let newCountries = {
			...countriesInSelection,
			[selectedCountry]: nrOfSelectedCompanies
		};

		let newSelectedCountry = selectedCountry;
		// If currently selected tab will have no companies now, switch away from it
		if (newCountries[selectedCountry] === 0) {
			const keys = Object.keys(newCountries);

			// Switch to another country index then the current one
			if (selectedCountry === keys[0]) {
				newSelectedCountry = keys[1] || keys[0];
			} else {
				newSelectedCountry = keys[0];
			}
			newSelectedCountry = newSelectedCountry === selectedCountry ? null : newSelectedCountry;
		}

		// eslint-disable-next-line no-unused-vars
		newCountries = Object.fromEntries(Object.entries(newCountries).filter(([_, value]) => value !== 0));

		if (Object.keys(newCountries).length === 0 || newSelectedCountry !== selectedCountry) {
			// If empty, reset
			dispatch(setSelectedResultCountryTab(newSelectedCountry));
		}
		dispatch({ type: SET_COUNTRIES_IN_SELECTION, countriesInSelection: newCountries });
	}
};

export const selectAllPage = () => (dispatch, getState) => {
	const {
		selection,
		[Tabs.Search]: { data }
	} = getState().ProspectingShared;
	const { country: selectedCountry } = getState().ProspectingManual;
	const idField = dispatch(getIdField());
	let count = 0;
	const newSelection = { ...selection };
	const alreadySelected = Object.keys(selection).length;

	for (const item of data) {
		const id = item[idField];

		if (!newSelection[id]) {
			if (alreadySelected + count >= selectionLimit(idField)) {
				warnSelectionLimit();
				break;
			}
			count += 1;
			newSelection[id] = { existing: existing(idField, item), country: selectedCountry };
		}
	}

	dispatch(handleCountriesInSelection(count, selectedCountry));
	dispatch({ type: SET_SELECTION, selection: newSelection });
	dispatch({ type: UPDATE_VIEW, view: Tabs.Selected, total: Object.keys(newSelection).length });
};

export const deSelectAllPage = () => (dispatch, getState) => {
	const {
		selection,
		[Tabs.Search]: { data }
	} = getState().ProspectingShared;
	const { country: selectedCountry } = getState().ProspectingManual;
	const idField = dispatch(getIdField());
	const newSelection = { ...selection };
	let count = 0;

	for (const item of data) {
		const id = item[idField];

		if (newSelection[id]) {
			count += 1;
			delete newSelection[id];
		}
	}

	// We want to remove countries so therefore negative
	dispatch(handleCountriesInSelection(-count, selectedCountry));
	dispatch({ type: SET_SELECTION, selection: newSelection });
	dispatch({ type: UPDATE_VIEW, view: Tabs.Selected, total: Object.keys(newSelection).length });
};

export const removeAllSelections = () => dispatch => {
	dispatch({ type: SET_SELECTION, selection: {} });
	dispatch({ type: UPDATE_VIEW, view: Tabs.Selected, total: 0, data: [] });
	dispatch({ type: SET_COUNTRIES_IN_SELECTION, countriesInSelection: {} });
	dispatch({ type: SET_SELECTED_COUNTRY_IN_RESULT, selectedCountryInResult: null });
};

export const excludeFromSelection = id => async (dispatch, getState) => {
	const {
		[Tabs.Selected]: { data },
		selectedCountryInResult: country
	} = getState().ProspectingShared;
	const { selection } = getState().ProspectingShared;
	const newSelection = { ...selection };
	const idField = dispatch(getIdField());
	delete newSelection[id];

	dispatch(handleCountriesInSelection(-1, country));
	dispatch({ type: SET_SELECTION, selection: newSelection });
	const filteredData = data.filter(company => company[idField] !== id);
	dispatch({ type: UPDATE_VIEW, view: Tabs.Selected, data: filteredData, total: Object.keys(newSelection).length });
};

export const toggleIndividualSelection = id => (dispatch, getState) => {
	const {
		selection,
		[Tabs.Search]: { data }
	} = getState().ProspectingShared;
	const { country: selectedCountry } = getState().ProspectingManual;
	const idField = dispatch(getIdField());
	const newSelection = { ...selection };
	let count = 1;

	if (newSelection[id]) {
		count = -1;
		delete newSelection[id];
	} else {
		if (Object.keys(selection).length >= selectionLimit(idField)) {
			warnSelectionLimit();
			return;
		}
		const item = data.find(item => item[idField] === id);
		newSelection[id] = { existing: existing(idField, item), country: selectedCountry };
	}

	dispatch(handleCountriesInSelection(count, selectedCountry));
	dispatch({ type: SET_SELECTION, selection: newSelection });
	dispatch({ type: UPDATE_VIEW, view: Tabs.Selected, total: Object.keys(newSelection).length });
};

export const addAllResults = () => async dispatch => {
	const filters = dispatch(ProspectingManual.getSearchFilters());
	if (!filters) {
		return;
	}
	const { q, country } = filters;

	try {
		// Counted here rather than read from the list, which can still show the search before an edit.
		const [all, newOnly] = await Promise.all([
			Prospecting.find({ q, country, limit: 1 }),
			Prospecting.find({ q: [...q, { a: 'notInUpsales', c: 'eq', v: true }], country, limit: 1 })
		]);
		const total = all.metadata.total;
		if (total > MAX_SEARCH_ADD) {
			return;
		}
		dispatch({ type: SET_ALL_RESULTS, allResults: { q, country, updates: total - newOnly.metadata.total } });
		dispatch({ type: UPDATE_VIEW, view: Tabs.Selected, total });
		dispatch(onHeaderTabChanged(Steps.Manage));
	} catch (error) {
		logError(error, 'ProspectingShared - count the new companies in the search');
	}
};

export const getSelectionFilter = () => (dispatch, getState) => {
	const { selection, allResults } = getState().ProspectingShared;
	if (allResults) {
		return { q: allResults.q };
	}
	const idField = dispatch(getIdField());

	const rb = new RequestBuilder();
	const ids = Object.keys(selection);

	if (ids.length) {
		rb.addFilter({ field: idField }, comparisonTypes.Equals, ids);
	}

	return rb.build();
};

export const getTotalUpdates = () => (dispatch, getState) => {
	const { selection, allResults } = getState().ProspectingShared;
	if (allResults) {
		return allResults.updates;
	}
	return Object.values(selection).reduce((res, item) => res + item.existing, 0);
};

export const changeTab = activeTab => (dispatch, getState) => {
	const { quickSearchValue } = getState().ProspectingShared;

	dispatch({ type: SET_ACTIVE_TAB, activeTab });

	if (activeTab === Tabs.Selected) {
		dispatch(searchSelected());
	} else if (quickSearchValue) {
		dispatch(search());
	}
};

export const onQuickSearchChanged = value => (dispatch, getState) => {
	const { activeTab } = getState().ProspectingShared;

	dispatch({ type: SET_QUICK_SERCH_VALUE, quickSearchValue: value });
	dispatch({ type: UPDATE_VIEW, view: Tabs.Search, offset: 0 });
	dispatch({ type: UPDATE_VIEW, view: Tabs.Selected, offset: 0 });

	if (activeTab === Tabs.Search) {
		dispatch(search());
	} else {
		dispatch(searchSelected());
	}
};

export const onPaginationChanged = offset => (dispatch, getState) => {
	const { activeTab } = getState().ProspectingShared;

	dispatch({ type: UPDATE_VIEW, view: activeTab, offset });

	if (activeTab === Tabs.Search) {
		dispatch(search());
	} else {
		dispatch(searchSelected());
	}
};

export const onSortChange =
	({ field, asc }) =>
	(dispatch, getState) => {
		const { activeTab } = getState().ProspectingShared;
		dispatch({ type: UPDATE_VIEW, view: activeTab, sorting: field, asc });

		if (activeTab === Tabs.Selected) {
			dispatch(searchSelected());
		} else {
			dispatch(search());
		}
	};

const ACTION_HANDLERS = {
	// eslint-disable-next-line no-unused-vars
	[UPDATE_VIEW]: (state, { type, view, ...props }) => ({ ...state, [view]: { ...state[view], ...props } }),
	[RESET]: () => ({ ...initialState }),
	[RESET_SEARCH]: state => ({
		...initialState,
		[Tabs.Selected]: {
			...state[Tabs.Selected]
		},
		selection: state.selection,
		countriesInSelection: state.countriesInSelection,
		selectedCountryInResult: state.selectedCountryInResult
	}),
	[SET_QUICK_SERCH_VALUE]: (state, { quickSearchValue }) => ({ ...state, quickSearchValue }),
	[SET_ACTIVE_TAB]: (state, { activeTab }) => ({ ...state, activeTab }),
	// Back to the search ends an "add all": the selection is what step 2 adds again.
	[SET_SELECT_HEADER_TAB]: (state, { selectedHeaderTab }) =>
		selectedHeaderTab === Steps.Search && state.allResults
			? {
					...state,
					selectedHeaderTab,
					allResults: null,
					[Tabs.Selected]: { ...state[Tabs.Selected], total: Object.keys(state.selection).length }
			  }
			: { ...state, selectedHeaderTab },
	[SET_ALL_RESULTS]: (state, { allResults }) => ({ ...state, allResults }),
	[SET_SELECTION]: (state, { selection }) => ({ ...state, selection }),
	[SET_COUNTRIES_IN_SELECTION]: (state, { countriesInSelection }) => ({ ...state, countriesInSelection }),
	[SET_SELECTED_COUNTRY_IN_RESULT]: (state, { selectedCountryInResult }) => ({ ...state, selectedCountryInResult })
};

const ProspectingSharedReducer = (state = initialState, action) => {
	const handler = ACTION_HANDLERS[action.type];
	return handler ? handler(state, action) : state;
};

export default ProspectingSharedReducer;
